/* ==================================================================
   Termos de responsabilidade técnica — fonte única
   ------------------------------------------------------------------
   O mesmo texto aparece em três lugares: o resumo no widget, a página
   `/termos` e o registro do aceite no banco. Uma cópia só, pelo mesmo
   motivo do preço: três redações do mesmo termo divergem, e aí o
   cliente aceitou uma coisa e a página publica outra.

   Este módulo é PURO — sem banco, sem rede, sem React. Isso é o que
   permite compilá-lo junto do motor para o navegador, onde o widget o
   consome. O `scripts/build-motor.mjs` quebra o build se algo com
   dependência de servidor entrar aqui.

   ORIGEM: cláusula de 12 itens enviada pelo Alan, adaptada de exclusão
   de responsabilidade para definição de escopo. O texto original, a
   adaptação e a conferência item a item estão em
   `saidas/projeto-art/clausula-responsabilidade.md`.
   ================================================================== */

/**
 * A versão do texto, e ela é gravada em cada pedido.
 *
 * **Trocar esta data é obrigatório sempre que o TEXTO mudar.** Aceite sem
 * saber a que redação se referia não prova nada: daqui a um ano a redação
 * terá mudado, e um pedido de hoje precisa continuar apontando para o que
 * a pessoa realmente leu. Por isso é data e não número de versão — a
 * pergunta que alguém vai fazer é "o que estava escrito em agosto?".
 */
export const VERSAO_TERMOS = "2026-08-30";

/** Aprovado pelo Welbert em 30/08. Enviado ao Alan no mesmo dia; se ele
 *  pedir mudança, muda aqui, e a `VERSAO_TERMOS` sobe junto. */
export const RESUMO_ACEITE = {
  chamada: "Você está contratando o documento técnico, não a execução da obra.",
  paragrafos: [
    "A Reflect On analisa a reforma que você descreveu, emite a ART e assina por ela. Contratar, " +
      "supervisionar e responder pelos profissionais que executarem a obra cabe a você.",
    "A ART cobre o que foi descrito aqui. Se a reforma mudar durante a execução, comunique a " +
      "Reflect On antes. Alteração que não passa por análise fica fora do que o documento cobre.",
  ],
  rotulo: "Li e aceito os termos de responsabilidade técnica.",
} as const;

export type BlocoDeTermo = { titulo: string; paragrafos: string[] };

/** O texto integral da página `/termos`. */
export const TERMOS: { intro: BlocoDeTermo; blocos: BlocoDeTermo[]; fecho: BlocoDeTermo } = {
  intro: {
    titulo: "O que você contrata aqui",
    paragrafos: [
      "A Reflect On Engenharia faz três coisas, e são as que estão descritas na ART: analisa a " +
        "reforma que você descreveu, emite a Anotação de Responsabilidade Técnica e assina o " +
        "documento, pelo engenheiro responsável.",
      "A responsabilidade técnica do engenheiro é sobre essas atividades e sobre o que está " +
        "escrito na ART. Ela não se estende à execução física da reforma.",
    ],
  },

  blocos: [
    {
      titulo: "A obra em si",
      paragrafos: [
        "A execução da reforma é sua e dos profissionais que você contratar: pedreiro, " +
          "eletricista, encanador, instalador, pintor ou empresa. A Reflect On não executa, não " +
          "acompanha presencialmente, não fiscaliza, não gerencia e não administra a obra, e não " +
          "supervisiona, diária ou periodicamente, quem trabalha nela.",
        "Isso vale para o que der errado na execução: erro de quem executou, serviço feito em " +
          "desacordo com o projeto, o memorial ou as orientações técnicas, e acidente ou dano " +
          "material ou pessoal ocorrido durante a obra. Quem responde é quem executou.",
      ],
    },
    {
      titulo: "Os profissionais que você contratar",
      paragrafos: [
        "As obrigações trabalhistas, previdenciárias, fiscais e contratuais dos profissionais e " +
          "empresas que executam a reforma são de quem os contratou. A fiscalização do cumprimento " +
          "das normas de segurança do trabalho por eles também não faz parte deste serviço, a " +
          "menos que esteja expressamente incluída no que você contratou e na respectiva ART.",
      ],
    },
    {
      titulo: "Os materiais",
      paragrafos: [
        "A conferência da qualidade, da quantidade e da conformidade dos materiais comprados e " +
          "usados na obra não faz parte deste serviço.",
      ],
    },
    {
      titulo: "O que mudar depois",
      paragrafos: [
        "A ART cobre o que foi descrito no momento em que ela foi emitida. Se você, quem executa a " +
          "obra ou um terceiro alterar alguma coisa depois, e essa alteração não for submetida à " +
          "nossa análise antes, ela fica fora do que o documento cobre.",
        "O mesmo vale para serviço executado sem o nosso conhecimento que altere as condições " +
          "consideradas na documentação técnica.",
      ],
    },
    {
      titulo: "O que você nos informou",
      paragrafos: [
        "O documento é feito a partir do que você descreve: medidas, informações, fotos, vídeos. " +
          "Se algum desses dados estiver incorreto, incompleto ou desatualizado, o documento " +
          "reflete o dado errado, e a responsabilidade por ele é de quem informou.",
      ],
    },
    {
      titulo: "O que não foi contratado",
      paragrafos: [
        "Instalações, estruturas e elementos construtivos que não são objeto da atividade técnica " +
          "contratada não entram na ART e não estão cobertos por ela.",
      ],
    },
    {
      titulo: "Sobre conversas e reuniões",
      paragrafos: [
        "Se houver reunião ou videochamada entre você e o engenheiro, ela tem caráter orientativo " +
          "e está ligada à atividade técnica contratada. Conversar sobre a obra não transforma " +
          "este serviço em acompanhamento, fiscalização ou gerenciamento dela.",
      ],
    },
  ],

  fecho: {
    titulo: "Em uma frase",
    paragrafos: [
      "A responsabilidade da Reflect On é sobre a atividade técnica contratada e descrita na ART, " +
        "e não pode ser interpretada como responsabilidade pela execução, integral ou parcial, da " +
        "reforma.",
    ],
  },
};
